import { Navigate, Route, Routes } from 'react-router-dom';
import { Layout } from '@components/Layout';
import { ProtectedRoute } from '@components/ProtectedRoute';
import { WhatsAppFab } from '@components/WhatsAppFab';
import { LandingPage } from '@pages/Landing';
import { LoginPage } from '@pages/Login';
import { ActivatePage } from '@pages/Activate';
import { ActivateBisPage } from '@pages/ActivateBis';
import { OfertaPage } from '@pages/Oferta';
import { CredencialPage } from '@pages/Credencial';
import { DescargaExportPage } from '@pages/DescargaExport';
import { MejoraPlanPage } from '@pages/MejoraPlan';
import { DashboardPage } from '@pages/Dashboard';
import { EmpresaDetallePage } from '@pages/EmpresaDetalle';
import { EmpresasPage } from '@pages/Empresas';
import { UsuariosPage } from '@pages/Usuarios';
import { PadronesPage } from '@pages/Padrones';
import { PadronDetallePage } from '@pages/PadronDetalle';
import { PadronDashboardPage } from '@pages/PadronDashboard';
import { AsociadosPage } from '@pages/Asociados';
import { ActividadPage } from '@pages/Actividad';
import { PerfilPage } from '@pages/Perfil';
import { LegajosListosPage } from '@pages/LegajosListos';
import { RechazadosPage } from '@pages/Rechazados';
import { ObservacionesPage } from '@pages/Observaciones';
import { PagosMs2Page } from '@pages/PagosMs2';
import { BusquedaPage } from '@pages/Busqueda';
import { TmkCrmPage } from '@pages/TmkCrm';
import { SkytelFunnelPage } from '@pages/SkytelFunnel';
import { CuestionariosSaludPage } from '@pages/CuestionariosSalud';
import { AdherentesPage } from '@pages/Adherentes';
import { AltaManualPage } from '@pages/AltaManual';
import { BloqueadosPage } from '@pages/Bloqueados';
import { BajasPage } from '@pages/Bajas';
import { ModificacionesPage } from '@pages/Modificaciones';
import { RecienNacidosPage } from '@pages/RecienNacidos';
import { AlertasBajasPage } from '@pages/AlertasBajas';
import { ReporteAbmWebPage } from '@pages/ReporteAbmWeb';
import { ReportePadronPage } from '@pages/ReportePadron';
import { ReglasPage } from '@pages/Reglas';
import { DocumentacionPage } from '@pages/Documentacion';
import { VentasComisionesPage } from '@pages/VentasComisiones';
import { NotFoundPage } from '@pages/NotFound';

// Las rutas del backoffice se gatean por PERMISO de vista (no por rol). El
// permiso efectivo lo trae el backend (defaults del rol ⊕ overrides por
// usuario); ver `lib/permissions/catalog.ts`. El SuperAdmin puede ajustar por
// usuario qué vistas ve cada uno desde la pantalla de Usuarios.
function App() {
  return (
    <Routes>
      {/* Vistas públicas. El botón de WhatsApp acompaña todo el flujo del
          titular (DNI → método → OTP → wizard → cierre); se monta junto a la
          Landing y al wizard de activación. */}
      <Route
        path="/"
        element={
          <>
            <LandingPage />
            <WhatsAppFab />
          </>
        }
      />
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/activar/:token"
        element={
          <>
            <ActivatePage />
            <WhatsAppFab />
          </>
        }
      />
      {/* Activación de un legajo_bis: el titular ya afiliado suma un nuevo
          integrante (Fase 2 alta-directa). OTP al titular del destino. */}
      <Route
        path="/activar-bis/:token"
        element={
          <>
            <ActivateBisPage />
            <WhatsAppFab />
          </>
        }
      />
      <Route path="/oferta/:token" element={<OfertaPage />} />
      {/* Consulta pública del número de credencial digital (Medicard) por DNI. */}
      <Route path="/credencial" element={<CredencialPage />} />
      {/* Autogestión pública del pase a MS2 por DNI. */}
      <Route path="/mejora-plan" element={<MejoraPlanPage />} />
      {/* Link de descarga de un export grande, el que llega por mail (VEN-595).
          El token viaja en el fragmento (#), que no llega a ningún servidor, y
          esta página lo reenvía por POST. Pública porque el mail se abre sin
          sesión; lo que autoriza es el token. */}
      <Route path="/descargas/:jobId" element={<DescargaExportPage />} />

      {/* Backoffice (autenticado) */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute requiereVista="view:dashboard">
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/empresas"
          element={
            <ProtectedRoute requiereVista="view:empresas">
              <EmpresasPage />
            </ProtectedRoute>
          }
        />
        {/* Detalle en su propia URL: se puede linkear y volver atrás, y hay
            lugar para subsecciones (datos, planes, DDJJ, usuarios). */}
        <Route
          path="/empresas/:id"
          element={
            <ProtectedRoute requiereVista="view:empresas">
              <EmpresaDetallePage />
            </ProtectedRoute>
          }
        />
        {/* El perfil propio no lleva permiso de vista: cualquier usuario
            autenticado ve el suyo. */}
        <Route
          path="/perfil"
          element={
            <ProtectedRoute>
              <PerfilPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/usuarios"
          element={
            <ProtectedRoute requiereVista="view:usuarios">
              <UsuariosPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/padrones"
          element={
            <ProtectedRoute requiereVista="view:padrones">
              <PadronesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/padrones/:id"
          element={
            <ProtectedRoute requiereVista="view:padron-detalle">
              <PadronDetallePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/padrones/:id/dashboard"
          element={
            <ProtectedRoute requiereVista="view:padron-dashboard">
              <PadronDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/asociados"
          element={
            <ProtectedRoute requiereVista="view:asociados">
              <AsociadosPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reglas"
          element={
            <ProtectedRoute requiereVista="view:reglas">
              <ReglasPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bloqueados"
          element={
            <ProtectedRoute requiereVista="view:bloqueados">
              <BloqueadosPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bajas"
          element={
            <ProtectedRoute requiereVista="view:bajas">
              <BajasPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/alertas-bajas"
          element={
            <ProtectedRoute requiereVista="view:alertas-bajas">
              <AlertasBajasPage />
            </ProtectedRoute>
          }
        />
        {/* Modificación de datos (F2-6). La ruta la gatea `view:modificaciones`
            —que Medicus también tiene, para seguir los trámites—; poder CARGAR
            cada flujo lo deciden las dos claves de acción, tanto en la pantalla
            como en el backend. */}
        <Route
          path="/modificaciones"
          element={
            <ProtectedRoute requiereVista="view:modificaciones">
              <ModificacionesPage />
            </ProtectedRoute>
          }
        />
        {/* Recién nacidos (VEN-513): Binary los tiene con datos básicos, el portal
            los sumó al legajo y la organización los completa. */}
        <Route
          path="/recien-nacidos"
          element={
            <ProtectedRoute requiereVista="view:recien-nacidos">
              <RecienNacidosPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reporte-abm-web"
          element={
            <ProtectedRoute requiereVista="view:reporte-abm-web">
              <ReporteAbmWebPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reporte-padron"
          element={
            <ProtectedRoute requiereVista="view:reporte-padron">
              <ReportePadronPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/legajos-listos"
          element={
            <ProtectedRoute requiereVista="view:legajos-listos">
              <LegajosListosPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/rechazados"
          element={
            <ProtectedRoute requiereVista="view:rechazados">
              <RechazadosPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/observaciones"
          element={
            <ProtectedRoute requiereVista="view:observaciones">
              <ObservacionesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pagos-ms2"
          element={
            <ProtectedRoute requiereVista="view:pagos-ms2">
              <PagosMs2Page />
            </ProtectedRoute>
          }
        />
        <Route
          path="/busqueda"
          element={
            <ProtectedRoute requiereVista="view:busqueda">
              <BusquedaPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tmk-crm"
          element={
            <ProtectedRoute requiereVista="view:tmk-crm">
              <TmkCrmPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/skytel-funnel"
          element={
            <ProtectedRoute requiereVista="view:skytel-funnel">
              <SkytelFunnelPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/legajos-bis"
          element={
            <ProtectedRoute requiereVista="view:adherentes">
              <AdherentesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cuestionarios-salud"
          element={
            <ProtectedRoute requiereVista="view:cuestionarios-salud">
              <CuestionariosSaludPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/alta-manual"
          element={
            <ProtectedRoute requiereVista="view:alta-manual">
              <AltaManualPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/actividad"
          element={
            <ProtectedRoute requiereVista="view:actividad">
              <ActividadPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/documentacion"
          element={
            <ProtectedRoute requiereVista="view:documentacion">
              <DocumentacionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ventas-comisiones"
          element={
            <ProtectedRoute requiereVista="view:ventas-comisiones">
              <VentasComisionesPage />
            </ProtectedRoute>
          }
        />
        <Route path="/auditoria" element={<Navigate to="/actividad" replace />} />
        {/* La vista se renombró de "/adherentes" a "/legajos-bis"; redirige los links viejos. */}
        <Route path="/adherentes" element={<Navigate to="/legajos-bis" replace />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default App;
